import type { TriageReview } from "@/types/triageReview";

/** Routing decision made before the model (web-demo-backend src/triage_routing.py). */
export interface TriageRouting {
  routed: boolean;
  reason: string;
  protocol_class?: string;
  study_description?: string | null;
  age_years?: number | null;
  age_recorded?: boolean;
  rule: { source: string; commit: string; interim: boolean; replaced_by: string } | null;
}

export type TriageDeviceState = "completed" | "processing" | "error" | "not_analyzed" | "missing";

/** A routed study's recorded device result. Absent for studies that were not routed. */
export interface TriageDeviceResult {
  state: TriageDeviceState;
  job_id?: string;
  duration_seconds?: number | null;
  message?: string | null;
  review?: TriageReview;
}

/**
 * Where and how urgently the study was ordered. On demo worklists this is simulated
 * order context set in the worklist config (web-demo-backend
 * `src/config/triage_worklists/`), never derived from the device result.
 */
export interface TriageOrderContext {
  location: "ED" | "Inpatient" | "Outpatient";
  priority: "STAT" | "Routine";
  simulated: true;
}

export interface TriageWorklistRow {
  study_uid: string;
  case_id: string;
  source_cohort_id: string;
  exam: string | null;
  age_years: number | null;
  age_recorded: boolean;
  routing: TriageRouting;
  device: TriageDeviceResult | null;
  /** `uid::job` that opens the study's images; never used to load a prediction for
   * a study that was not routed. Null when no job exists to open the images. */
  image_study_id: string | null;
  /** Null when the worklist sets none; absent from backends that predate it. */
  order_context?: TriageOrderContext | null;
}

/** `ED · STAT`, or null when the study has no order context. */
export function orderContextText(row: TriageWorklistRow): string | null {
  const order = row.order_context;
  return order ? `${order.location} · ${order.priority}` : null;
}

/** Whether any row carries order context (its columns and label show only then). */
export function hasOrderContext(rows: readonly TriageWorklistRow[]): boolean {
  return rows.some((row) => row.order_context != null);
}

export interface TriageWorklistResponse {
  meta: {
    id: string;
    name: string;
    description: string;
    device_model: string;
    /** The device as its indications name it, e.g. "a2z-Abdo-Triage". */
    device_display_name: string;
    device_cohort_id: string;
  };
  studies: TriageWorklistRow[];
}

export interface TriageWorklistSummaryEntry {
  id: string;
  name: string;
  description: string;
  device_model: string;
  count: number;
}

/** What one row shows in its Status column. */
export type TriageRowStatus =
  | { kind: "not_routed"; reason: string }
  | { kind: "processing"; elapsedSeconds: number | null }
  | { kind: "flagged"; findings: string[] }
  | { kind: "no_flag" }
  | { kind: "not_analyzed"; message: string }
  | { kind: "error"; message: string };

/** Status of a row from its recorded result (no replay). */
export function rowStatus(row: TriageWorklistRow): TriageRowStatus {
  if (!row.routing.routed) return { kind: "not_routed", reason: row.routing.reason };
  const device = row.device;
  if (!device || device.state === "missing")
    return { kind: "error", message: device?.message ?? "No device result recorded" };
  if (device.state === "processing") return { kind: "processing", elapsedSeconds: null };
  if (device.state === "error")
    return { kind: "error", message: device.message ?? "Inference failed" };
  if (device.state === "not_analyzed")
    return { kind: "not_analyzed", message: device.message ?? "Not analyzed by the device" };
  const flagged = device.review?.flagged ?? [];
  return flagged.length > 0
    ? { kind: "flagged", findings: flagged.map((flag) => flag.display_name) }
    : { kind: "no_flag" };
}

/**
 * Arrival order, with flagged studies moved up when `flaggedFirst` is on.
 * Stable: flagged rows keep their arrival order among themselves, and every other
 * row keeps its arrival order — nothing is moved down relative to another
 * unflagged row, and nothing is removed.
 */
export function orderRows<T>(
  rows: readonly T[],
  isFlagged: (row: T) => boolean,
  flaggedFirst: boolean,
): T[] {
  if (!flaggedFirst) return [...rows];
  return [...rows.filter(isFlagged), ...rows.filter((row) => !isFlagged(row))];
}

/** Replay clock: arrivals every `arrivalSpacing` replay seconds; processing takes the
 *  recorded duration divided by `speed`. Returns null before the row has arrived. */
export function replayStatus(
  row: TriageWorklistRow,
  index: number,
  replaySeconds: number,
  opts: { arrivalSpacing: number; speed: number },
): TriageRowStatus | null {
  const arrivedAt = index * opts.arrivalSpacing;
  if (replaySeconds < arrivedAt) return null;
  const recorded = rowStatus(row);
  if (recorded.kind === "not_routed") return recorded;
  const duration = row.device?.duration_seconds;
  if (duration == null) return recorded;
  const elapsedReplay = replaySeconds - arrivedAt;
  if (elapsedReplay * opts.speed < duration)
    return { kind: "processing", elapsedSeconds: Math.floor(elapsedReplay * opts.speed) };
  return recorded;
}

/** `93` -> `1m 33s` */
export function formatSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

/** Study uids in the worklist's default order (flagged first), as the list opens. */
export function defaultCaseOrder(rows: readonly TriageWorklistRow[]): string[] {
  return orderRows(rows, (row) => rowStatus(row).kind === "flagged", true).map(
    (row) => row.study_uid,
  );
}

/**
 * The case order a case page steps through: the order the worklist was showing
 * when the case was opened (`shown`), or the default order when none was passed
 * or it does not hold this study. Uids no longer on the worklist are dropped.
 */
export function caseOrder(
  rows: readonly TriageWorklistRow[],
  studyUid: string,
  shown: readonly string[] | undefined,
): string[] {
  const onList = new Set(rows.map((row) => row.study_uid));
  const order = (shown ?? []).filter((uid) => onList.has(uid));
  return order.includes(studyUid) ? order : defaultCaseOrder(rows);
}

/** Previous (-1) or next (+1) case in `order`, wrapping at the ends. Null when
 * the study is not in the order or the order has no other case. */
export function adjacentCase(order: readonly string[], studyUid: string, step: -1 | 1) {
  const index = order.indexOf(studyUid);
  if (index === -1 || order.length < 2) return null;
  return order[(index + step + order.length) % order.length];
}

/** Router state the worklist passes to a case page: the study uids in shown order. */
export interface TriageCaseNavState {
  order: string[];
}
